import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';

const EMPTY_FORM = { question: '', options: ['', '', '', ''], correctIndex: 0, explanation: '' };

function formFromQuestion(q) {
  const correctIndex = q.options.indexOf(q.correctAnswer);
  return {
    question: q.question,
    options: [...q.options],
    correctIndex: correctIndex >= 0 ? correctIndex : 0,
    explanation: q.explanation || '',
  };
}

function QuestionForm({ form, setForm, onSave, onCancel, saving, error }) {
  const handleOptionChange = (i, value) => {
    setForm((f) => {
      const options = [...f.options];
      options[i] = value;
      return { ...f, options };
    });
  };

  return (
    <form onSubmit={onSave} className="qcard-form">
      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="form-group">
        <label>Vraag</label>
        <textarea
          value={form.question}
          onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
          placeholder="Typ hier de vraag..."
          rows={3}
          required
        />
      </div>

      <label className="form-section-label">Opties</label>
      <div className="options-grid">
        {form.options.map((opt, i) => (
          <div className="form-group" key={i}>
            <label>
              Optie {String.fromCharCode(65 + i)}
              {form.correctIndex === i && <span className="correct-indicator"> ✓</span>}
            </label>
            <input
              type="text"
              value={opt}
              onChange={(e) => handleOptionChange(i, e.target.value)}
              placeholder={`Optie ${String.fromCharCode(65 + i)}`}
              required
            />
          </div>
        ))}
      </div>

      <div className="form-group">
        <label>Correct antwoord</label>
        <select
          value={form.correctIndex}
          onChange={(e) => setForm((f) => ({ ...f, correctIndex: parseInt(e.target.value) }))}
        >
          {form.options.map((opt, i) => (
            <option key={i} value={i}>
              {String.fromCharCode(65 + i)} — {opt || '(leeg)'}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Uitleg</label>
        <textarea
          value={form.explanation}
          onChange={(e) => setForm((f) => ({ ...f, explanation: e.target.value }))}
          placeholder="Leg uit waarom dit het juiste antwoord is..."
          rows={4}
        />
      </div>

      <div className="qcard-form-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Annuleren</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>
    </form>
  );
}

export default function QuestionsManager() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/questions')
      .then(({ data }) => setQuestions(data))
      .catch(() => setError('Kon vragen niet laden'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (q) => {
    setEditingId(q.id);
    setEditForm(formFromQuestion(q));
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setError('');
  };

  const validate = (form) => {
    if (!form.question.trim()) return 'Vraag is verplicht';
    if (form.options.some((o) => !o.trim())) return 'Alle opties moeten ingevuld zijn';
    return null;
  };

  const buildPayload = (form) => ({
    question: form.question.trim(),
    options: form.options.map((o) => o.trim()),
    correctAnswer: form.options[form.correctIndex].trim(),
    explanation: form.explanation.trim(),
  });

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const err = validate(editForm);
    if (err) return setError(err);
    setSaving(true);
    setError('');
    try {
      await api.put(`/api/questions/${editingId}`, buildPayload(editForm));
      setEditingId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdd = async (e) => {
    e.preventDefault();
    const err = validate(addForm);
    if (err) return setError(err);
    setSaving(true);
    setError('');
    try {
      await api.post('/api/questions', buildPayload(addForm));
      setAddModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/questions/${id}`);
      setDeleteConfirm(null);
      if (editingId === id) setEditingId(null);
      load();
    } catch {
      setError('Verwijderen mislukt');
    }
  };

  const filtered = questions.filter((q) =>
    q.question.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Header */}
      <div className="admin-section-header">
        <div>
          <h2 className="admin-section-title">Vragen</h2>
          <p className="admin-section-sub">{questions.length} vragen in de databank</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setAddForm(EMPTY_FORM); setError(''); setAddModal(true); }}>
          + Toevoegen
        </button>
      </div>

      {/* Search */}
      <div className="admin-search-bar">
        <input
          type="text"
          placeholder="Zoek op vraag..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && <span className="search-count">{filtered.length} resultaten</span>}
      </div>

      {/* Question cards */}
      {loading ? (
        <div className="admin-loading">Laden...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '32px' }}>
          Geen vragen gevonden
        </div>
      ) : (
        <div className="qcard-list">
          {filtered.map((q) => (
            <div key={q.id} className={`qcard ${editingId === q.id ? 'qcard--editing' : ''}`}>
              {/* Collapsed view */}
              {editingId !== q.id && (
                <>
                  <div className="qcard-id">#{q.id}</div>
                  <p className="qcard-question">{q.question}</p>
                  <div className="qcard-footer">
                    <span className="correct-answer-badge">{q.correctAnswer}</span>
                    <div className="action-btns">
                      <button className="btn-sm btn-edit" onClick={() => openEdit(q)}>Bewerken</button>
                      <button className="btn-sm btn-danger" onClick={() => setDeleteConfirm(q)}>Verwijder</button>
                    </div>
                  </div>
                </>
              )}

              {/* Expanded edit form */}
              {editingId === q.id && (
                <>
                  <div className="qcard-edit-header">
                    <span className="qcard-id">#{q.id} — Bewerken</span>
                    <button className="btn-sm btn-secondary" onClick={cancelEdit}>Sluiten</button>
                  </div>
                  <QuestionForm
                    form={editForm}
                    setForm={setEditForm}
                    onSave={handleSaveEdit}
                    onCancel={cancelEdit}
                    saving={saving}
                    error={error}
                  />
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add modal */}
      {addModal && (
        <div className="modal-overlay" onClick={() => setAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Vraag toevoegen</h3>
            <QuestionForm
              form={addForm}
              setForm={setAddForm}
              onSave={handleSaveAdd}
              onCancel={() => setAddModal(false)}
              saving={saving}
              error={error}
            />
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Vraag verwijderen?</h3>
            <p style={{ color: '#64748b', marginBottom: 8 }}>Dit kan niet ongedaan worden gemaakt.</p>
            <p style={{ fontStyle: 'italic', color: '#1e293b', marginBottom: 24 }}>
              "{deleteConfirm.question.slice(0, 100)}{deleteConfirm.question.length > 100 ? '…' : ''}"
            </p>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Annuleren</button>
              <button className="btn btn-danger-solid" onClick={() => handleDelete(deleteConfirm.id)}>Verwijderen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
